import React from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Backbutton from "../components/Backbutton";
import "./AboutUs.css";
import {
    Wifi,
    Shield,
    Headphones,
    MapPin,
    Phone,
    Mail,
    Users,
    Award,
    Zap,
    CheckCircle,
    Building2,
    Star,
    Globe,
    Clock
} from "lucide-react";

function AboutUs() {
    const stats = [
        { icon: <Users size={22} />, number: "500+", label: "Happy Customers" },
        { icon: <Zap size={22} />, number: "200Mbps", label: "Max Speed" },
        { icon: <Clock size={22} />, number: "24/7", label: "Support Available" },
        { icon: <Star size={22} />, number: "99.9%", label: "Uptime Guarantee" },
    ];

    const values = [
        {
            icon: <Zap size={26} />,
            title: "Fast & Reliable",
            desc: "We deliver committed high-speed fiber internet directly to your home, ensuring consistent speeds you can count on every day.",
        },
        {
            icon: <Shield size={26} />,
            title: "Trusted Service",
            desc: "Our network is built on a secure, stable fiber-optic infrastructure backed by years of experience serving the Achampet community.",
        },
        {
            icon: <Headphones size={26} />,
            title: "Responsive Support",
            desc: "Our local support team is always ready to assist you promptly. We pride ourselves on quick resolution and friendly service.",
        },
        {
            icon: <Globe size={26} />,
            title: "Affordable Plans",
            desc: "We offer a wide range of plans at competitive prices, ensuring every home and business can enjoy quality broadband without breaking the bank.",
        },
        {
            icon: <CheckCircle size={26} />,
            title: "100% Fiber",
            desc: "End-to-end fiber connectivity ensures you get the fastest and most reliable internet experience, free from the limitations of traditional copper cables.",
        },
        {
            icon: <Award size={26} />,
            title: "Community First",
            desc: "As a local ISP, we are deeply committed to keeping our community in Achampet connected and empowered through technology.",
        },
    ];

    return (
        <div className="about-page-root">
            <Header />
            <Backbutton />
            {/* Hero */}
            <section className="about-hero">
                <div className="about-hero-container">
                    <div className="about-hero-badge">
                        <Building2 size={14} />
                        About Ritu Broadband
                    </div>
                    <h1>
                        Connecting Achampet with <span>High-Speed Internet</span>
                    </h1>
                    <p className="about-hero-subtitle">
                        A trusted local broadband internet service provider dedicated to delivering fast,
                        reliable, and affordable internet connectivity to homes and businesses.
                    </p>
                </div>
            </section>

            <div className="about-body">

                {/* Stats */}
                <div className="about-stats">
                    {stats.map((s, i) => (
                        <div className="about-stat-card" key={i}>
                            <div className="about-stat-icon">{s.icon}</div>
                            <div className="about-stat-number">{s.number}</div>
                            <div className="about-stat-label">{s.label}</div>
                        </div>
                    ))}
                </div>

                {/* Who We Are */}
                <section>
                    <div className="about-section-header">
                        <div className="about-section-label">
                            <Wifi size={13} /> Who We Are
                        </div>
                        <h2>Your Trusted Local ISP in Achampet</h2>
                        <p>Dedicated to delivering fast, reliable, and affordable internet connectivity since day one.</p>
                    </div>

                    <div className="about-who-grid">
                        <div className="about-who-text">
                            <p>
                                <strong>Ritu Broadband</strong> is a trusted broadband internet service provider
                                dedicated to delivering fast, reliable, and affordable internet connectivity to
                                homes and businesses in Achampet and surrounding areas.
                            </p>
                            <p>
                                We are committed to providing <strong>quality internet services</strong>, responsive
                                customer support, and a smooth online experience for our customers. Our goal is to
                                keep our community connected with dependable broadband services.
                            </p>
                            <p>
                                From students and work-from-home professionals to small businesses, we understand
                                how important a stable connection is. That's why we invest in 100% fiber
                                infrastructure to ensure you always get the speeds you pay for.
                            </p>
                        </div>

                        <div className="about-highlight-card">
                            <div className="about-highlight-item">
                                <div className="about-highlight-icon"><Wifi size={20} /></div>
                                <div>
                                    <h4>100% Fiber to Home</h4>
                                    <p>Pure fiber-optic cables bring ultrafast internet directly to your doorstep with zero copper degradation.</p>
                                </div>
                            </div>
                            <div className="about-highlight-item">
                                <div className="about-highlight-icon"><Zap size={20} /></div>
                                <div>
                                    <h4>Committed Speed</h4>
                                    <p>We provide dedicated bandwidth so you always get the promised speed on your Wi-Fi router.</p>
                                </div>
                            </div>
                            <div className="about-highlight-item">
                                <div className="about-highlight-icon"><Headphones size={20} /></div>
                                <div>
                                    <h4>Local Customer Support</h4>
                                    <p>Our Achampet-based team responds quickly to ensure minimal downtime and maximum satisfaction.</p>
                                </div>
                            </div>
                            <div className="about-highlight-item">
                                <div className="about-highlight-icon"><Shield size={20} /></div>
                                <div>
                                    <h4>Secure Network</h4>
                                    <p>Your connection is protected with enterprise-grade security to keep your data safe at all times.</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Our Values */}
                <section>
                    <div className="about-section-header">
                        <div className="about-section-label">
                            <Star size={13} /> Our Values
                        </div>
                        <h2>What Sets Us Apart</h2>
                        <p>We don't just provide internet — we provide a commitment to quality, reliability, and community.</p>
                    </div>

                    <div className="about-values-grid">
                        {values.map((v, i) => (
                            <div className="about-value-card" key={i}>
                                <div className="about-value-icon-wrap">{v.icon}</div>
                                <h3>{v.title}</h3>
                                <p>{v.desc}</p>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Business Address */}
                <section className="about-address-section">
                    <div className="about-address-left">
                        <div className="about-address-badge">
                            <MapPin size={12} /> Our Location
                        </div>
                        <h2>Visit Our Office</h2>
                        <p>
                            We are conveniently located in Achampet, Telangana. Drop by our office
                            for new connections, plan upgrades, billing queries, or any support you need.
                        </p>
                    </div>

                    <div className="about-address-card">
                        <div className="about-address-row">
                            <div className="about-address-row-icon"><Building2 size={18} /></div>
                            <div className="about-address-row-content">
                                <h4>Business Name</h4>
                                <p>Ritu Broadband</p>
                            </div>
                        </div>

                        <div className="about-address-divider" />

                        <div className="about-address-row">
                            <div className="about-address-row-icon"><MapPin size={18} /></div>
                            <div className="about-address-row-content">
                                <h4>Address</h4>
                                <p>
                                    Plot No. 20-80/A,<br />
                                    Opposite New Bus Stand,<br />
                                    Ar Reddy Chicken Center,<br />
                                    Billing – 1st Floor,<br />
                                    Achampet
                                </p>
                            </div>
                        </div>

                        <div className="about-address-divider" />

                        <div className="about-address-row">
                            <div className="about-address-row-icon"><Phone size={18} /></div>
                            <div className="about-address-row-content">
                                <h4>Phone</h4>
                                <p>7702935830 / 8500168970</p>
                            </div>
                        </div>

                        <div className="about-address-divider" />

                        <div className="about-address-row">
                            <div className="about-address-row-icon"><Mail size={18} /></div>
                            <div className="about-address-row-content">
                                <h4>Email</h4>
                                <p>ritubroadband123@gmail.com</p>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CTA */}
                <div className="about-contact-section">
                    <h2>Ready to Get Connected?</h2>
                    <p>Explore our plans and experience fast, reliable broadband in Achampet today.</p>
                    <div className="about-contact-btns">
                        <Link to="/plans" className="about-btn-primary">
                            <Zap size={17} /> View Plans
                        </Link>
                        <a href="tel:7702935830" className="about-btn-secondary">
                            <Phone size={17} /> Call Us Now
                        </a>
                    </div>
                </div>

            </div>

            <Footer />
        </div>
    );
}

export default AboutUs;
